import React from 'react';
import { themes, pageTypeMeta } from './themes';
import { PawPrint, Heart, Stethoscope, Camera, DollarSign, Phone, GraduationCap } from 'lucide-react';

const PetPage = ({ page, pageIndex, totalPages, theme }) => {
  const t = themes[theme] || themes['modern-minimal'];
  const meta = pageTypeMeta[page.pageType] || { label: 'Pet', color: t.accent };

  const renderHealth = () => {
    const healthFields = page.healthFields || page.fields || ['Date', 'Type', 'Vet name', 'Notes', 'Next appointment'];
    return (
      <div className="flex-1 overflow-hidden">
        {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontStyle: 'italic', color: t.subtext }}>{page.content}</p>}
        <div className="space-y-2">
          {healthFields.map((f, fi) => (
            <div key={fi}>
              <div className="flex items-center gap-1.5 mb-0.5">
                <Stethoscope className="w-2 h-2" style={{ color: t.accent }} />
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', fontWeight: 600, color: t.accent }}>{f}</span>
              </div>
              <div className="h-5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTraining = () => {
    const entries = page.trainingEntries || [];
    return (
      <div className="flex-1 overflow-hidden">
        {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontStyle: 'italic', color: t.subtext }}>{page.content}</p>}
        {entries.length > 0 ? (
          <div className="space-y-1.5">
            {entries.map((e, ei) => (
              <div key={ei} className="rounded-lg p-2" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '10'}` }}>
                <div className="flex items-center justify-between mb-0.5">
                  <div className="flex items-center gap-1.5">
                    <GraduationCap className="w-2.5 h-2.5" style={{ color: t.accent }} />
                    <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontWeight: 600, color: t.text }}>{e.command}</span>
                  </div>
                  <span className="text-[7px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: e.status === 'Mastered' ? '#50B86C15' : t.accent + '15', color: e.status === 'Mastered' ? '#50B86C' : t.accent }}>{e.status}</span>
                </div>
                {e.notes && <p style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.subtext }}>{e.notes}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-1.5">
            {(page.fields || []).map((f, fi) => (
              <div key={fi} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.accent }} />
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text }}>{f}</span>
                <div className="flex-1 h-5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderPhoto = () => (
    <div className="flex-1 flex flex-col">
      <div className="rounded-xl flex flex-col items-center justify-center mb-3" style={{ backgroundColor: t.fieldBg || t.bg, border: `2px dashed ${t.accent}25`, minHeight: '7rem' }}>
        <Camera className="w-6 h-6 mb-1.5" style={{ color: t.accent + '40' }} strokeWidth={1.5} />
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.accent + '60' }}>Place photo of your pet here</span>
      </div>
      <div className="space-y-1.5">
        {(page.fields || ['Date', 'Caption', 'Memory']).map((f, fi) => (
          <div key={fi} className="flex items-center gap-2">
            <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, minWidth: '3rem' }}>{f}:</span>
            <div className="flex-1 h-4 rounded" style={{ borderBottom: `1px dashed ${t.accent}25` }} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderProfile = () => (
    <div className="flex-1 overflow-hidden">
      <div className="flex gap-3 mb-3">
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: t.fieldBg || t.bg, border: `2px dashed ${t.accent}25` }}>
          <PawPrint className="w-6 h-6" style={{ color: t.accent + '40' }} />
        </div>
        <div className="flex-1 space-y-1.5">
          {(page.fields || ['Name', 'Breed', 'Birthday', 'Weight']).slice(0, 4).map((f, fi) => (
            <div key={fi}>
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', fontWeight: 600, color: t.accent }}>{f}</span>
              <div className="h-4 rounded-lg mt-0.5" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      </div>
      {(page.fields || []).slice(4).map((f, fi) => (
        <div key={fi} className="mb-1.5">
          <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', fontWeight: 600, color: t.accent }}>{f}</span>
          <div className="h-5 rounded-lg mt-0.5" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
        </div>
      ))}
    </div>
  );

  const renderDefault = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-2 leading-relaxed" style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', color: t.subtext, lineHeight: 1.6 }}>{page.content}</p>}
      {page.fields && page.fields.length > 0 && (
        <div className="space-y-1.5">
          {page.fields.map((f, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <PawPrint className="w-2 h-2 flex-shrink-0" style={{ color: t.accent }} />
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text, fontWeight: 500 }}>{f}</span>
              <div className="flex-1 h-5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const contentMap = {
    'pet-intro': renderProfile,
    'pet-health': renderHealth,
    'pet-training': renderTraining,
    'pet-photo': renderPhoto,
    'pet-memories': renderPhoto,
  };

  const render = contentMap[page.pageType] || renderDefault;

  return (
    <div data-testid={`document-page-${pageIndex}`} className="w-full aspect-[210/297] flex flex-col relative overflow-hidden" style={{ backgroundColor: t.cardBg }}>
      <div className="h-1 w-full" style={{ backgroundColor: meta.color + '40' }} />
      <div className="px-7 pt-4 pb-2 flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <PawPrint className="w-3 h-3" style={{ color: meta.color }} />
            <span className="text-[8px] font-bold tracking-[0.15em] uppercase" style={{ fontFamily: t.bodyFont, color: meta.color }}>{meta.label}</span>
          </div>
          <h2 style={{ fontFamily: t.font, fontWeight: 700, fontSize: 'clamp(1rem, 2.5vw, 1.3rem)', color: t.text, lineHeight: 1.2 }}>{page.title}</h2>
        </div>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.subtext, opacity: 0.5 }}>{pageIndex + 1}/{totalPages}</span>
      </div>
      <div className="px-7 flex-1 overflow-hidden flex flex-col">{render()}</div>
      <div className="px-7 py-2 flex items-center justify-between" style={{ borderTop: `1px solid ${meta.color}10` }}>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4, letterSpacing: '0.1em' }}>LUMERA STUDIO</span>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4 }}>Page {pageIndex + 1}</span>
      </div>
    </div>
  );
};

export default PetPage;
